import { useState } from 'react';
import { changeAdminPassword } from '@/lib/auth';

export default function AdminSettingsPage() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all required fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long.');
      return;
    }

    setLoading(true);

    const res = await changeAdminPassword(currentPassword, newPassword, confirmPassword);
    setLoading(false);

    if (res.success) {
      setSuccess(res.message || 'Password changed successfully! A security alert email was sent to the admin email address.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setError(res.error || 'Failed to change password. Please verify current password.');
    }
  };

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <div className="text-xs font-mono text-neon-400 mb-1">// SECURITY SETTINGS</div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-white">Settings & Security</h1>
        <p className="text-sm text-white/50 mt-1">Manage your admin security, credentials, and authentication preferences.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {/* Navigation / Info Sidebar */}
        <div className="glass rounded-xl p-6 border border-white/5 space-y-4 h-fit">
          <div className="flex items-center gap-3 text-neon-300 font-semibold text-sm">
            <i className="ri-shield-keyhole-line text-lg" />
            Security & Password
          </div>
          <div className="text-xs text-white/60 leading-relaxed">
            Changing your password will hash your new credentials securely, invalidate older login sessions, and dispatch a security notification email to your admin address.
          </div>
          <div className="p-3 rounded-lg bg-white/5 border border-white/5 text-xs text-white/50 space-y-1">
            <div><strong className="text-white">Admin Email:</strong> nabilasundas6@gmail.com</div>
            <div><strong className="text-white">Hash Algorithm:</strong> bcrypt</div>
          </div>
        </div>

        {/* Change Password Form */}
        <div className="md:col-span-2 glass rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
          <div className="border-b border-white/5 pb-4">
            <h2 className="font-display font-semibold text-lg text-white">Change Admin Password</h2>
            <p className="text-xs text-white/50 mt-0.5">Enter your current password to verify identity before setting a new password.</p>
          </div>

          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-start gap-2">
              <i className="ri-error-warning-line text-lg shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm flex items-start gap-2">
              <i className="ri-checkbox-circle-line text-lg shrink-0 mt-0.5" />
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs font-semibold text-white/70 mb-2">Current Password *</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-12 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-1 transition"
                >
                  <i className={showCurrent ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">New Password *</label>
                <div className="relative">
                  <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-12 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                    placeholder="Min 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-1 transition"
                  >
                    <i className={showNew ? 'ri-eye-off-line' : 'ri-eye-line'} />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-white/70 mb-2">Confirm New Password *</label>
                <div className="relative">
                  <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full pl-10 pr-12 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
            </div>

            <div className="pt-3">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary text-white font-semibold px-8 py-3.5 rounded-lg text-sm whitespace-nowrap inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Updating Password...
                  </>
                ) : (
                  <>
                    Update Admin Password
                    <i className="ri-shield-check-line" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
